
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Filter, X, ChevronDown, Star, Clock, Globe, Layers, BookOpen, Heart, Share2, Grid, List, ArrowUpDown } from "lucide-react";
import { useTranslation } from "react-i18next";
import moment from "moment/moment";
import { courseService } from "../../../_services";
import CartButton from "../../Courses/CourseTile/CartButton/CartButton";
import Share from "../../Courses/CourseShare/courseShare";
import { deleteWishlist, addWishlist } from "../../../_actions/cart.actions";
import { courseActions } from "../../../_actions";
import { getS3URL } from "../../../_helpers";
import "../../Program/ExploreProgram.scss";
import "./search.scss";

const stripHtml = (html) => {
  if (!html) return "";
  const doc = new DOMParser().parseFromString(html, "text/html");
  return doc.body.textContent || "";
};

const getShortDescription = (course) => {
  if (course.courseOneLiner) return course.courseOneLiner;
  const plainText = stripHtml(course.Description);
  return plainText.length > 120 ? plainText.substring(0, 120) + "..." : plainText;
};

function SearchPanel() {
  const { t } = useTranslation();
  const location = useLocation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const allcourse = useSelector((state) => state?.course?.courses?.all);
  const { loggedIn } = useSelector((state) => state?.authentication || {});
  const queryParams = new URLSearchParams(location.search);

  const newItemType = queryParams.get("new-item-type");

  const [fetchCourse, setFetchCourse] = useState([]);
  const [filters, setFilters] = useState({
    category: [],
    level: [],
    language: [],
  });

  const [selectedFilters, setSelectedFilters] = useState({
    category: [],
    level: [],
    language: [],
  });
  const [expandedDescriptions, setExpandedDescriptions] = useState({});
  const [showFilters, setShowFilters] = useState(false);
  const [visibleItems, setVisibleItems] = useState(6);
  const [viewMode, setViewMode] = useState("grid"); // grid or normal
  const [sortBy, setSortBy] = useState("relevant"); // relevant, newest, highest-rated, duration-asc, duration-desc
  const [shareCourse, setShareCourse] = useState(null);

  const likeHandler = (e, courseId, isWishlisted) => {
    e.stopPropagation(); // Prevent card navigation
    if (loggedIn) {
      dispatch(courseActions.handleResetCourseList());
      if (isWishlisted) {
        dispatch(deleteWishlist(courseId));
      } else {
        dispatch(addWishlist(courseId));
      }
    } 
    else {
      navigate("/login");
    }
  };

  const searchCourse = newItemType
    ? allcourse?.courses.filter((course) =>
      [course.CourseName, course.Description, course.category, course.courseOneLiner].some((field) =>
        field?.toLowerCase().includes(newItemType?.toLowerCase()),
      ),
    )
    : allcourse?.courses;

  const filteredCourses = searchCourse?.filter((course) => {
    if (
      selectedFilters.category.length === 0 &&
      selectedFilters.level.length === 0 &&
      selectedFilters.language.length === 0
    ) {
      return true;
    }

    const categoryMatch = selectedFilters.category.length === 0 || selectedFilters.category.includes(course.category);
    const levelMatch = selectedFilters.level.length === 0 || selectedFilters.level.includes(course.Level);
    const languageMatch = selectedFilters.language.length === 0 || selectedFilters.language.includes(course.Language);

    return categoryMatch && levelMatch && languageMatch;
  });

  const sortedAndFilteredCourses = [...(filteredCourses || [])].sort((a, b) => {
    const cleanSortBy = sortBy ? sortBy.trim().split(" ")[0] : "relevant";

    if (cleanSortBy === "newest") {
      const dateA = a.CreatedOn || a.Createdat ? new Date(a.CreatedOn || a.Createdat).getTime() : 0;
      const dateB = b.CreatedOn || b.Createdat ? new Date(b.CreatedOn || b.Createdat).getTime() : 0;
      return isNaN(dateB) || isNaN(dateA) ? 0 : dateB - dateA;
    }
    if (cleanSortBy === "highest-rated") {
      const ratingA = parseFloat(a.Rating || a.StarRating || a.rating) || 0;
      const ratingB = parseFloat(b.Rating || b.StarRating || b.rating) || 0;
      return ratingB - ratingA;
    }
    if (cleanSortBy === "duration-asc") {
      const durA = parseFloat(a.Duration) || 0;
      const durB = parseFloat(b.Duration) || 0;
      return durA - durB;
    }
    if (cleanSortBy === "duration-desc") {
      const durA = parseFloat(a.Duration) || 0;
      const durB = parseFloat(b.Duration) || 0;
      return durB - durA;
    }
    if (cleanSortBy === "name-asc") {
      return (a.CourseName || "").localeCompare(b.CourseName || "");
    }
    if (cleanSortBy === "name-desc") {
      return (b.CourseName || "").localeCompare(a.CourseName || "");
    }
    return 0;
  });

  const paginatedCourses = sortedAndFilteredCourses?.slice(0, visibleItems);
  const hasMoreCourses = sortedAndFilteredCourses?.length > visibleItems;

  const loadMoreCourses = () => {
    setVisibleItems((prev) => Math.min(prev + 6, sortedAndFilteredCourses?.length || 0));
  };

  const fetchCourses = async (course) => {
    try {
      const data = await courseService.fetchCourseById("course", course.ID);
      return data;
    } catch (err) {
      console.log("error", err);
      return null;
    }
  };

  const fetchAllCourses = async () => {
    if (!searchCourse?.length) return;

    try {
      const results = await Promise.all(searchCourse.map((course) => fetchCourses(course)));
      const filteredResults = results.filter((course) => course !== null);
      setFetchCourse((prevCourses) => [...prevCourses, ...filteredResults]);
      console.log("Fetched courses:", results);
      addcourses(filteredResults);
    } catch (error) {
      console.log("Error fetching courses", error);
    }
  };

  function addcourses(coursesList = fetchCourse) {
    if (coursesList && coursesList.length > 0) {
      const uniqueCourses = coursesList.filter(
        (course, index, self) => index === self.findIndex((c) => c.ID === course.ID),
      );

      const categoryCounts = uniqueCourses.reduce((acc, course) => {
        if (course.category) {
          acc[course.category] = (acc[course.category] || 0) + 1;
        }
        return acc;
      }, {});

      const levelCounts = uniqueCourses.reduce((acc, course) => {
        if (course.Level) {
          acc[course.Level] = (acc[course.Level] || 0) + 1;
        }
        return acc;
      }, {});

      const languageCounts = uniqueCourses.reduce((acc, course) => {
        if (course.Language) {
          acc[course.Language] = (acc[course.Language] || 0) + 1;
        }
        return acc;
      }, {});

      setFilters({
        category: Object.entries(categoryCounts).map(([name, count]) => ({ name, count })),
        level: Object.entries(levelCounts).map(([name, count]) => ({ name, count })),
        language: Object.entries(languageCounts).map(([name, count]) => ({ name, count })),
      });
    }
  }

  useEffect(() => {
    if (fetchCourse && fetchCourse.length > 0) {
      addcourses(fetchCourse);
    }
  }, [fetchCourse]);

  useEffect(() => {
    fetchAllCourses();
    console.log("fetchAllCourses called");
  }, []);

  useEffect(() => {
    setVisibleItems(6);
  }, [selectedFilters]);

  const handleFilterChange = (filterType, filterName) => {
    setSelectedFilters((prev) => ({
      ...prev,
      [filterType]: prev[filterType].includes(filterName)
        ? prev[filterType].filter((name) => name !== filterName)
        : [...prev[filterType], filterName],
    }));
  };

  const clearAllFilters = () => {
    setSelectedFilters({
      category: [],
      level: [],
      language: [],
    });

  };
  

  const toggleFilters = () => {
    setShowFilters(!showFilters);
  };

  const toggleDescription = (courseId, event) => {
    event.stopPropagation();
    setExpandedDescriptions((prev) => ({
      ...prev,
      [courseId]: !prev[courseId],
    }));
  };

  const slugify = (text) => {
    return text
      .toString()
      .toLowerCase()
      .replace(/\s+/g, "-")
      .replace(/[^\w-]+/g, "")
      .replace(/--+/g, "-")
      .replace(/^-+/, "")
      .replace(/-+$/, "");
  };

  const handleClick = (course) => {
    const slug = slugify(course.CourseName);
    const id = course.ID;
    // navigate(`/${slug}/${id}`, { state: { course: course } });
    navigate(`/${slug}`, { state: { course: course } });
  };

  return (
    <div className="flex flex-col lg:flex-row gap-6 px-4 md:px-8 lg:px-16 py-6 lg:py-10 mt-16 lg:mt-24 bg-gray-50 min-h-screen">
      {/* Mobile Filter Button and Options */}
      <div className="lg:hidden flex flex-col gap-3 mb-6 w-full">
        <div className="flex justify-between items-center">
          <h1 className="text-lg font-bold text-gray-800">
            {sortedAndFilteredCourses?.length} {sortedAndFilteredCourses?.length === 1 ? "Program" : "Programs"} found {newItemType ? `for "${newItemType}"` : null}
          </h1>
          <button
            onClick={toggleFilters}
            className="flex items-center gap-2 bg-white px-4 py-2 rounded-lg text-gray-700 shadow-sm border border-gray-200"
          >
            <Filter size={18} />
            <span>Filters</span>
          </button>
        </div>
        
        <div className="flex items-center justify-between gap-3 bg-white p-0 rounded-xl border border-gray-200 shadow-sm">
          {/* Sort Dropdown */}
          <div className="flex items-center gap-2 flex-1">
            <ArrowUpDown size={16} className="text-gray-400" />
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="text-sm  m-0 p-0 font-medium text-gray-700 bg-transparent border-none outline-none cursor-pointer w-full focus:ring-0"
            >
              <option value="relevant">Most Relevant</option>
              <option value="newest">Newest First</option>
              <option value="highest-rated">Highest Rated</option>
              <option value="name-asc">Name: A → Z</option>
              <option value="name-desc">Name: Z → A</option>
              <option value="duration-asc">Duration: Low to High</option>
              <option value="duration-desc">Duration: High to Low</option>
            </select>
          </div>
          {/* View Toggle */}
          <div className="flex items-center bg-gray-100 rounded-lg p-0.5 border border-gray-200">
            <button
              onClick={() => setViewMode("grid")}
              className={`p-1.5 rounded-md transition-colors ${viewMode === "grid" ? "bg-white text-gray-900 shadow-sm" : "text-gray-500 hover:text-gray-900"}`}
              title="Grid View"
            >
              <Grid size={16} />
            </button>
            <button
              onClick={() => setViewMode("normal")}
              className={`p-1.5 rounded-md transition-colors ${viewMode === "normal" ? "bg-white text-gray-900 shadow-sm" : "text-gray-500 hover:text-gray-900"}`}
              title="Normal View"
            >
              <List size={16} />
            </button>
          </div>
        </div>
      </div>

      {/* Left Side - Filters */}
      <div
        className={`${showFilters ? "fixed inset-0 z-50 bg-white p-6 overflow-y-auto" : "hidden"} lg:block lg:w-72 lg:bg-white lg:rounded-xl lg:shadow-sm lg:p-6 lg:border lg:border-gray-100 lg:sticky lg:top-28 lg:h-fit`}
      >
        <div className="flex justify-between items-start mb-6">
          <h2 className="text-xl font-bold text-gray-800">Filters</h2>
          <div className="flex items-center gap-2">
            <button
              className="text-dark-600 hover:text-gray-800 text-sm font-medium"
              onClick={clearAllFilters}
            >
              Clear all
            </button>
            <button className="lg:hidden text-gray-500" onClick={toggleFilters}>
              <X size={24} />
            </button>
          </div>
        </div>

        {Object.keys(filters).map((filterKey, idx) => (
          <div key={idx} className="mb-6">
            <div className="flex items-center gap-2 mb-3">
              {filterKey === 'category' && <Layers size={18} className="text-gray-500" />}
              {filterKey === 'level' && <BookOpen size={18} className="text-gray-500" />}
              {filterKey === 'language' && <Globe size={18} className="text-gray-500" />}
              <h3 className="font-bold text-gray-700 capitalize">{filterKey}</h3>
            </div>
            <div className="flex flex-col space-y-2">
              {filters[filterKey].map(({ name, count }, index) => (
                <label key={index} className="flex items-center gap-2 hover:bg-gray-50 rounded-lg">
                  <input
                    type="checkbox"
                    className="!w-5 h-5 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                    checked={selectedFilters[filterKey].includes(name)}
                    onChange={() => handleFilterChange(filterKey, name)}
                  />
                  <span className="text-gray-700 flex-1">{name}</span>
                  <span className="text-gray-500 text-sm">({count})</span>
                </label>
              ))}
            </div>
            {idx < Object.keys(filters).length - 1 && <div className="border-b border-gray-100 my-4"></div>}
          </div>
        ))}
      </div>

      {/* Right Side - Course List */}
      <div className="flex-1">
        {/* Desktop Header & Controls */}
        <div className="hidden lg:flex justify-between items-center gap-4 mb-6 pb-4 border-b border-gray-200">
          <h1 className="text-2xl font-bold text-gray-800">
            {sortedAndFilteredCourses?.length} {sortedAndFilteredCourses?.length === 1 ? "Program" : "Programs"} found {newItemType ? `for "${newItemType}"` : ""}
          </h1>
          
          <div className="flex items-center gap-3">
            {/* Sort Dropdown */}
            <div className="flex items-center gap-2 bg-white px-3 py-0 rounded-lg border border-gray-200 shadow-sm">
              <ArrowUpDown size={16} className="text-gray-400" />
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="text-sm font-medium text-gray-700 bg-transparent border-none outline-none cursor-pointer focus:ring-0"
              >
                <option value="relevant">Most Relevant</option>
                <option value="newest">Newest First</option>
                <option value="highest-rated">Highest Rated</option>
                <option value="duration-asc">Duration: Low to High</option>
                <option value="duration-desc">Duration: High to Low</option>
              </select>
            </div>

            {/* View Toggle */}
            <div className="flex items-center bg-gray-100 rounded-lg p-0.5 border border-gray-200">
              <button
                onClick={() => setViewMode("grid")}
                className={`p-2 rounded-md transition-colors ${viewMode === "grid" ? "bg-white text-gray-900 shadow-sm" : "text-gray-500 hover:text-gray-900"}`}
                title="Grid View"
              >
                <Grid size={18} />
              </button>
              <button
                onClick={() => setViewMode("normal")}
                className={`p-2 rounded-md transition-colors ${viewMode === "normal" ? "bg-white text-gray-900 shadow-sm" : "text-gray-500 hover:text-gray-900"}`}
                title="Normal View"
              >
                <List size={18} />
              </button>
            </div>
          </div>
        </div>

        {viewMode === "grid" ? (
          <div className="courses-grid !p-0 !gap-6">
            {paginatedCourses?.map((course, index) => (
              <div
                key={index}
                className="course-card"
                onClick={() => handleClick(course)}
              >
                {/* Tag Ribbon */}
                <div className="ribbon-wrapper">
                  <span className="ribbon-tag">
                    {course.courseType || "Online"}
                  </span>
                </div>

                {/* Floating Action Overlay (Wishlist & Share) */}
                <div className="card-floating-actions" onClick={(e) => e.stopPropagation()}>
                  <button
                    className={`floating-btn wishlist-btn ${course?.isWishlisted ? "active" : ""}`}
                    onClick={(e) => likeHandler(e, course.ID, course?.isWishlisted)}
                    title={course?.isWishlisted ? "Remove from Wishlist" : "Add to Wishlist"}
                  >
                    <Heart size={15} fill={course?.isWishlisted ? "#ef4444" : "none"} stroke={course?.isWishlisted ? "#ef4444" : "currentColor"} />
                  </button>
                  <button
                    className="floating-btn share-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      setShareCourse(course);
                    }}
                    title="Share Program"
                  >
                    <Share2 size={15} />
                  </button>
                </div>

                <div className="card-top-row">
                  <div className="image-container">
                    <img
                      src={course.imageFiles && course.imageFiles.length > 0 ? getS3URL(course.imageFiles[0].fileName) : ""}
                      alt={course.CourseName}
                      className="course-image"
                      loading="lazy"
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = require("../../../assets/images/bgimages.webp");
                      }}
                    />
                  </div>
                  <h2 className="course-title">{course.CourseName}</h2>
                </div>

                <div className="card-stats-row">
                  <span className="stat-item">
                    <strong>Duration:</strong> {course.Duration} hrs
                  </span>
                  <span className="stat-item">
                    <strong>Starts:</strong>{" "}
                    {moment(course?.Createdat).format("MMM DD, YYYY")}
                  </span>
                </div>

                <div className="card-description">
                  <p>{getShortDescription(course)}</p>
                </div>

                <div className="card-actions-row">
                  <button
                    className="view-button"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleClick(course);
                    }}
                  >
                    View Course
                  </button>
                  <div className="buy-button-wrapper" onClick={(e) => e.stopPropagation()}>
                    {loggedIn ? (
                      <CartButton post={course} />
                    ) : (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate("/login");
                        }}
                      >
                        <span className="add-to-cart">{t("addToCart")}</span>
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="flex flex-col gap-6">
            {paginatedCourses?.map((course, index) => (
              <div
                key={index}
                className="course-card normal-view"
                onClick={() => handleClick(course)}
              >
                {/* Tag Ribbon */}
                <div className="ribbon-wrapper">
                  <span className="ribbon-tag">
                    {course.courseType || "Online"}
                  </span>
                </div>

                {/* Floating Action Overlay (Wishlist & Share) */}
                <div className="card-floating-actions" onClick={(e) => e.stopPropagation()}>
                  <button
                    className={`floating-btn wishlist-btn ${course?.isWishlisted ? "active" : ""}`}
                    onClick={(e) => likeHandler(e, course.ID, course?.isWishlisted)}
                    title={course?.isWishlisted ? "Remove from Wishlist" : "Add to Wishlist"}
                  >
                    <Heart size={15} fill={course?.isWishlisted ? "#ef4444" : "none"} stroke={course?.isWishlisted ? "#ef4444" : "currentColor"} />
                  </button>
                  <button
                    className="floating-btn share-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      setShareCourse(course);
                    }}
                    title="Share Program"
                  >
                    <Share2 size={15} />
                  </button>
                </div>

                {/* Left Side: Image and Title */}
                <div className="card-top-row">
                  <div className="image-container">
                    <img
                      src={course.imageFiles && course.imageFiles.length > 0 ? getS3URL(course.imageFiles[0].fileName) : ""}
                      alt={course.CourseName}
                      className="course-image"
                      loading="lazy"
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = require("../../../assets/images/bgimages.webp");
                      }}
                    />
                  </div>
                  <h2 className="course-title">{course.CourseName}</h2>
                </div>

                {/* Middle Content: Stats and Description */}
                <div className="card-content-wrapper">
                  <div className="card-stats-row">
                    <span className="stat-item">
                      <strong>Duration:</strong> {course.Duration} hrs
                    </span>
                    <span className="stat-item">
                      <strong>Starts:</strong>{" "}
                      {moment(course?.Createdat).format("MMM DD, YYYY")}
                    </span>
                  </div>
                  <div className="card-description">
                    <p>{getShortDescription(course)}</p>
                  </div>
                </div>

                {/* Right Side: Rating & Actions */}
                <div className="card-right-wrapper">
                  {course.Rating && (
                    <div className="flex items-center gap-1 text-sm text-gray-700 mb-4 self-start md:self-end">
                      <Star size={16} className="text-yellow-400 fill-yellow-400" />
                      <span className="font-bold">{course.Rating} Rating</span>
                    </div>
                  )}
                  <div className="card-actions-row">
                    <button
                      className="view-button"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleClick(course);
                      }}
                    >
                      View Course
                    </button>
                    <div className="buy-button-wrapper" onClick={(e) => e.stopPropagation()}>
                      {loggedIn ? (
                        <CartButton post={course} />
                      ) : (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate("/login");
                          }}
                        >
                          <span className="add-to-cart">{t("addToCart")}</span>
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Show More Button */}
        {hasMoreCourses && (
          <div className="flex justify-center mt-10">
            <button
              onClick={loadMoreCourses}
              className="flex items-center gap-2 px-6 py-3 bg-white text-gray-600 rounded-lg hover:bg-blue-50 transition-colors border border-gray-200 shadow-sm"
            >
              <span>Load More Programs</span>
              <ChevronDown size={18} />
            </button>
          </div>
        )}

        {/* Showing X of Y Results */}
        <div className="text-center text-gray-500 mt-6 text-sm">
          Showing {Math.min(visibleItems, sortedAndFilteredCourses?.length || 0)} of {sortedAndFilteredCourses?.length || 0} programs
        </div>
      </div>

      {/* Overlay for mobile filters */}
      {showFilters && (
        <div className="lg:hidden fixed inset-0 bg-black bg-opacity-50 z-40" onClick={toggleFilters}></div>
      )}

      {shareCourse && (
        <Share
          shareUrl={`${window.location.origin}/${slugify(shareCourse.CourseName)}`}
          handleClose={() => setShareCourse(null)}
        />
      )}
    </div>
  );
}

export default SearchPanel;
